/* THEBLACKBOXHUB — global.js
   Core utilities, drawers, header, cart (AJAX + Section Rendering API), product form.
   Vanilla ES2017+, no dependencies. */
(function () {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  theme.routes = theme.routes || {};
  theme.strings = theme.strings || {};
  window.theme = theme;

  /* ---------- Utilities ---------- */
  const FOCUSABLE =
    'summary, a[href], button:enabled, [tabindex]:not([tabindex="-1"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe';

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0
    );
  }

  const trapState = { container: null, handler: null };

  function trapFocus(container, elementToFocus) {
    removeTrapFocus();
    trapState.container = container;
    trapState.handler = function (event) {
      if (event.key !== 'Tab') return;
      const els = getFocusable(container);
      if (!els.length) {
        event.preventDefault();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !container.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapState.handler);
    const target = elementToFocus || getFocusable(container)[0] || container;
    if (target && typeof target.focus === 'function') {
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        target.focus();
      }
    }
  }

  function removeTrapFocus(elementToFocus) {
    if (trapState.handler) document.removeEventListener('keydown', trapState.handler);
    trapState.container = null;
    trapState.handler = null;
    if (elementToFocus && typeof elementToFocus.focus === 'function' && document.body.contains(elementToFocus)) {
      try {
        elementToFocus.focus({ preventScroll: true });
      } catch (e) {
        elementToFocus.focus();
      }
    }
  }

  let lockCount = 0;
  function lockScroll() {
    lockCount += 1;
    document.body.classList.add('is-locked');
  }
  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.classList.remove('is-locked');
  }

  function debounce(fn, wait) {
    let t;
    return function () {
      const args = arguments;
      const ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function fetchConfig(type) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: type === 'javascript' ? 'application/javascript' : 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    };
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    thousands = typeof thousands === 'undefined' ? ',' : thousands;
    decimal = typeof decimal === 'undefined' ? '.' : decimal;
    if (isNaN(number) || number == null) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    format = format || (theme.showCurrencyCode ? theme.moneyWithCurrencyFormat : theme.moneyFormat) || '₹{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return format;
    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  let toastTimer;
  function showToast(message, isError) {
    const toast = document.querySelector('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.toggle('toast--error', !!isError);
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.hidden = true;
    }, 4000);
  }

  const subscribers = {};
  function subscribe(eventName, callback) {
    (subscribers[eventName] = subscribers[eventName] || []).push(callback);
    return function () {
      subscribers[eventName] = (subscribers[eventName] || []).filter((cb) => cb !== callback);
    };
  }
  function publish(eventName, data) {
    (subscribers[eventName] || []).forEach(function (cb) {
      try {
        cb(data);
      } catch (e) {
        /* keep other subscribers running */
      }
    });
  }

  function parseHTML(text) {
    return new DOMParser().parseFromString(text, 'text/html');
  }

  function storageGet(key, fallback) {
    try {
      const value = window.localStorage.getItem(key);
      return value === null ? fallback : JSON.parse(value);
    } catch (e) {
      return fallback;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* storage unavailable */
    }
  }

  window.themeUtils = {
    trapFocus,
    removeTrapFocus,
    lockScroll,
    unlockScroll,
    debounce,
    fetchConfig,
    formatMoney,
    escapeHtml,
    showToast,
    subscribe,
    publish,
    parseHTML,
    storageGet,
    storageSet,
  };

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  /* ---------- Drawer base ---------- */
  class ThemeDrawer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.panel = this.querySelector('.drawer__panel') || this;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          event.stopPropagation();
          this.close();
        }
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      this.removeAttribute('aria-hidden');
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        lockScroll();
        trapFocus(this.panel, this.querySelector('[data-autofocus]') || this.panel);
      });
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'false');
      removeTrapFocus(this.trigger);
      this.dispatchEvent(new CustomEvent('drawer:closed', { bubbles: true }));
    }
  }
  window.ThemeDrawer = ThemeDrawer;
  define('theme-drawer', ThemeDrawer);

  /* Drawer that wraps <details> so it also works without JavaScript */
  class DetailsDrawer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.details = this.querySelector('details');
      this.summary = this.details && this.details.querySelector('summary');
      this.drawer = this.details && this.details.querySelector('.drawer');
      if (!this.details || !this.summary || !this.drawer) return;
      this.panel = this.drawer.querySelector('.drawer__panel');
      this.summary.setAttribute('aria-expanded', 'false');
      this.summary.addEventListener('click', (event) => {
        event.preventDefault();
        this.details.open ? this.close() : this.open();
      });
      this.drawer.addEventListener('click', (event) => {
        if (event.target.closest('[data-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.details.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          event.stopPropagation();
          this.close();
        }
      });
    }

    open() {
      this.details.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
      lockScroll();
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        const focusTarget = this.drawer.querySelector('[data-autofocus]') || this.panel;
        trapFocus(this.panel, focusTarget);
      });
      this.dispatchEvent(new CustomEvent('drawer:opened', { bubbles: true }));
    }

    close() {
      if (!this.details.open) return;
      this.drawer.classList.remove('is-open');
      this.summary.setAttribute('aria-expanded', 'false');
      unlockScroll();
      removeTrapFocus(this.summary);
      const done = () => {
        this.details.open = false;
      };
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      reduced ? done() : setTimeout(done, 320);
    }
  }
  define('details-drawer', DetailsDrawer);

  /* ---------- Desktop header menu (details based) ---------- */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.items = Array.from(this.querySelectorAll(':scope details'));
      this.hoverIntent = this.dataset.hover === 'true' && window.matchMedia('(hover: hover)').matches;
      this.items.forEach((details) => {
        const summary = details.querySelector('summary');
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        details.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
          if (details.open) this.closeOthers(details);
        });
        if (this.hoverIntent) {
          let timer;
          const li = details.closest('li') || details;
          li.addEventListener('mouseenter', () => {
            clearTimeout(timer);
            if (!details.open) {
              details.open = true;
              details._hoverOpenedAt = Date.now();
            }
          });
          summary.addEventListener('click', (event) => {
            /* A click right after hover-open should not close the menu */
            if (details.open && details._hoverOpenedAt && Date.now() - details._hoverOpenedAt < 1500) {
              event.preventDefault();
              details._hoverOpenedAt = 0;
            }
          });
          li.addEventListener('mouseleave', () => {
            timer = setTimeout(() => {
              details.open = false;
            }, 160);
          });
        }
      });
      this._onDocClick = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      this._onKey = (event) => {
        if (event.key !== 'Escape') return;
        const open = this.items.find((d) => d.open);
        if (open) {
          open.open = false;
          open.querySelector('summary').focus();
        }
      };
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKey);
      this.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.closeAll();
        }, 0);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKey);
    }

    closeOthers(current) {
      this.items.forEach((d) => {
        if (d !== current) d.open = false;
      });
    }

    closeAll() {
      this.items.forEach((d) => {
        d.open = false;
      });
    }
  }
  define('header-menu', HeaderMenu);

  /* ---------- Sticky header: exposes heights as CSS vars ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.wrapper = this.closest('.shopify-section') || this;
      this.sticky = this.dataset.sticky === 'true';
      if (this.sticky) this.wrapper.classList.add('section-header-wrapper', 'is-sticky');
      else this.wrapper.classList.add('section-header-wrapper');
      this.update = this.update.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.update();
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.update);
        this.ro.observe(this);
      } else {
        window.addEventListener('resize', debounce(this.update, 150));
      }
      if (this.sticky) window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }

    update() {
      const h = Math.round(this.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-height', h + 'px');
      document.documentElement.style.setProperty('--header-offset', this.sticky ? h + 'px' : '0px');
    }

    onScroll() {
      const scrolled = window.scrollY > 4;
      if (scrolled !== this._scrolled) {
        this._scrolled = scrolled;
        this.wrapper.classList.toggle('is-scrolled', scrolled);
      }
    }
  }
  define('sticky-header', StickyHeader);

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.track = this.querySelector('[data-rotating]');
      this.messages = Array.from(this.querySelectorAll('.announcement-bar__message'));
      this.index = 0;
      this.speed = Math.max(3, parseInt(this.dataset.speed || '5', 10)) * 1000;

      const dismissKey = 'tbbh-announcement-dismissed';
      if (this.dataset.dismissible === 'true') {
        try {
          if (window.sessionStorage.getItem(dismissKey) === this.dataset.hash && !window.Shopify?.designMode) {
            this.closest('.shopify-section').hidden = true;
            return;
          }
        } catch (e) {
          /* ignore */
        }
      }

      const closeBtn = this.querySelector('[data-dismiss]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          try {
            window.sessionStorage.setItem(dismissKey, this.dataset.hash || '1');
          } catch (e) {
            /* ignore */
          }
          const section = this.closest('.shopify-section');
          if (section) section.hidden = true;
        });
      }

      if (!this.track || this.messages.length < 2) {
        if (this.messages[0]) this.messages[0].classList.add('is-active');
        return;
      }
      this.show(0);
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => this.go(-1));
      if (next) next.addEventListener('click', () => this.go(1));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    show(i) {
      this.index = (i + this.messages.length) % this.messages.length;
      this.messages.forEach((m, n) => {
        const active = n === this.index;
        m.classList.toggle('is-active', active);
        m.setAttribute('aria-hidden', active ? 'false' : 'true');
        m.querySelectorAll('a').forEach((a) => (a.tabIndex = active ? 0 : -1));
        if (m.tagName === 'A') m.tabIndex = active ? 0 : -1;
      });
    }

    go(step) {
      this.show(this.index + step);
    }

    start() {
      if (this.messages.length < 2 || this.dataset.autoplay === 'false') return;
      this.stop();
      this.timer = setInterval(() => this.go(1), this.speed);
    }

    stop() {
      clearInterval(this.timer);
    }
  }
  define('announcement-bar', AnnouncementBar);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-qty]');
        if (!btn || btn.disabled) return;
        event.preventDefault();
        const step = parseInt(this.input.step, 10) || 1;
        const min = parseInt(this.input.min, 10) || 0;
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const current = parseInt(this.input.value, 10) || min;
        let next = btn.dataset.qty === 'plus' ? current + step : current - step;
        next = Math.min(Math.max(next, min), max);
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.input.addEventListener('change', () => this.validate());
      this.validate();
    }

    validate() {
      const min = parseInt(this.input.min, 10) || 0;
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const value = parseInt(this.input.value, 10);
      const minus = this.querySelector('[data-qty="minus"]');
      const plus = this.querySelector('[data-qty="plus"]');
      if (minus) minus.disabled = !(value > min);
      if (plus) plus.disabled = !(value < max);
    }
  }
  define('quantity-input', QuantityInput);

  /* ---------- Cart ---------- */
  function getSectionsToRender() {
    const sections = [];
    if (document.getElementById('CartDrawer')) sections.push('cart-drawer');
    if (document.getElementById('cart-icon-bubble')) sections.push('cart-icon-bubble');
    document.querySelectorAll('[data-main-cart]').forEach((el) => {
      if (el.dataset.sectionId) sections.push(el.dataset.sectionId);
    });
    return sections;
  }

  function renderCartSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach((id) => {
      const html = sections[id];
      if (typeof html !== 'string') return;
      const doc = parseHTML(html);
      if (id === 'cart-drawer') {
        const target = document.getElementById('CartDrawer-Inner');
        const source = doc.getElementById('CartDrawer-Inner');
        if (target && source) target.innerHTML = source.innerHTML;
      } else if (id === 'cart-icon-bubble') {
        const target = document.getElementById('cart-icon-bubble');
        const source = doc.querySelector('.shopify-section');
        if (target && source) target.innerHTML = source.innerHTML;
      } else {
        const target = document.getElementById('MainCart-' + id);
        const source = doc.getElementById('MainCart-' + id);
        if (target && source) target.innerHTML = source.innerHTML;
      }
    });
  }

  function refreshCart() {
    const ids = getSectionsToRender();
    if (!ids.length) return Promise.resolve();
    return fetch(theme.routes.root + '?sections=' + ids.join(','))
      .then((r) => r.json())
      .then((sections) => {
        renderCartSections(sections);
        publish('cart:updated', { source: 'refresh' });
      })
      .catch(() => {});
  }

  theme.cart = { getSectionsToRender, renderCartSections, refreshCart };

  class CartDrawer extends ThemeDrawer {
    connectedCallback() {
      super.connectedCallback();
      if (this._cartBound) return;
      this._cartBound = true;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-cart-trigger]');
        if (!trigger) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        this.open(trigger);
      });
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target && event.target.id === 'shopify-section-cart-drawer') this.open();
      });
    }

    renderContents(sections) {
      renderCartSections(sections);
      this.open();
    }
  }
  define('cart-drawer', CartDrawer);

  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.timers = {};
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-line-quantity]');
        if (!input) return;
        const line = input.dataset.line;
        clearTimeout(this.timers[line]);
        this.timers[line] = setTimeout(() => this.onQuantityChange(input), 350);
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-remove-line]');
        if (!remove) return;
        event.preventDefault();
        this.updateQuantity(parseInt(remove.dataset.removeLine, 10), 0);
      });
    }

    onQuantityChange(input) {
      const line = parseInt(input.dataset.line, 10);
      let quantity = parseInt(input.value, 10);
      if (isNaN(quantity) || quantity < 0) quantity = 0;
      this.updateQuantity(line, quantity, input);
    }

    updateQuantity(line, quantity, input) {
      const item = this.querySelector('[data-cart-item="' + line + '"]');
      if (item) item.classList.add('is-loading');
      const focusedId = document.activeElement && document.activeElement.id;
      const body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: getSectionsToRender(),
        sections_url: window.location.pathname,
      });
      fetch(theme.routes.cartChange + '.js', Object.assign(fetchConfig(), { body: body }))
        .then((response) => response.json().then((data) => ({ ok: response.ok, data: data })))
        .then(({ ok, data }) => {
          if (!ok || data.errors || data.status) {
            const message = data.description || data.message || data.errors || theme.strings.cartError;
            this.showLineError(line, typeof message === 'string' ? message : theme.strings.cartError);
            if (input) input.value = input.defaultValue;
            if (item) item.classList.remove('is-loading');
            return;
          }
          renderCartSections(data.sections);
          publish('cart:updated', { source: 'cart-items', cart: data });
          if (focusedId) {
            const el = document.getElementById(focusedId);
            if (el) el.focus({ preventScroll: true });
          }
          const updatedItem = data.items && data.items[line - 1];
          if (quantity > 0 && updatedItem && updatedItem.quantity < quantity) {
            this.showLineError(line, theme.strings.quantityError.replace('[quantity]', updatedItem.quantity));
          }
        })
        .catch(() => {
          if (item) item.classList.remove('is-loading');
          this.showLineError(line, theme.strings.network);
        });
    }

    showLineError(line, message) {
      const key = this.dataset.key || 'main';
      const el = document.querySelector('[data-line-error="' + key + '-' + line + '"]');
      if (el) {
        el.textContent = message;
        el.hidden = false;
      } else {
        showToast(message, true);
      }
    }
  }
  define('cart-items', CartItems);

  class CartNote extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const textarea = this.querySelector('textarea');
      if (!textarea) return;
      textarea.addEventListener(
        'input',
        debounce(() => {
          fetch(
            theme.routes.cartUpdate + '.js',
            Object.assign(fetchConfig(), { body: JSON.stringify({ note: textarea.value }) })
          ).catch(() => showToast(theme.strings.network, true));
        }, 500)
      );
    }
  }
  define('cart-note', CartNote);

  /* ---------- Product form (add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.form = this.querySelector('form');
      if (!this.form) return;
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) idInput.disabled = false;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.errorEl = this.querySelector('[data-form-error]');
    }

    getSubmitButtons() {
      const id = this.form.id;
      const buttons = Array.from(this.form.querySelectorAll('[type="submit"]'));
      if (id) buttons.push(...document.querySelectorAll('[type="submit"][form="' + id + '"]'));
      return buttons;
    }

    onSubmit(event) {
      if (theme.cartType === 'page' && !this.dataset.forceAjax) {
        return; /* native submit -> /cart */
      }
      event.preventDefault();
      const buttons = this.getSubmitButtons();
      if (buttons.some((b) => b.getAttribute('aria-disabled') === 'true' || b.classList.contains('is-loading'))) return;
      this.setError(null);
      buttons.forEach((b) => {
        b.classList.add('is-loading');
        b.setAttribute('aria-disabled', 'true');
      });

      const formData = new FormData(this.form);
      const sections = getSectionsToRender();
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);

      fetch(theme.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data: data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) {
            this.setError(data.description || data.message || theme.strings.cartError);
            return;
          }
          if (theme.cartType === 'page') {
            window.location.href = theme.routes.cart;
            return;
          }
          const modal = this.closest('quick-add-modal');
          if (modal && typeof modal.close === 'function') modal.close(true);
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') {
            drawer.renderContents(data.sections);
          } else {
            renderCartSections(data.sections);
            showToast(theme.strings.added);
          }
          publish('cart:updated', { source: 'product-form', item: data });
        })
        .catch(() => this.setError(theme.strings.network))
        .finally(() => {
          buttons.forEach((b) => {
            b.classList.remove('is-loading');
            if (!b.dataset.unavailable) b.removeAttribute('aria-disabled');
          });
        });
    }

    setError(message) {
      if (!this.errorEl) {
        if (message) showToast(message, true);
        return;
      }
      this.errorEl.hidden = !message;
      this.errorEl.textContent = message || '';
    }
  }
  define('product-form', ProductForm);

  /* ---------- Scroller prev/next buttons ---------- */
  class ScrollerControls extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const id = this.getAttribute('aria-controls');
      this.scroller = id ? document.getElementById(id) : null;
      if (!this.scroller) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      const step = (dir) => {
        const first = this.scroller.firstElementChild;
        const width = first ? first.getBoundingClientRect().width : this.scroller.clientWidth * 0.8;
        const isRtl = getComputedStyle(this.scroller).direction === 'rtl';
        this.scroller.scrollBy({ left: dir * (isRtl ? -1 : 1) * (width + 16), behavior: 'smooth' });
      };
      if (this.prev) this.prev.addEventListener('click', () => step(-1));
      if (this.next) this.next.addEventListener('click', () => step(1));
      this.update = this.update.bind(this);
      this.scroller.addEventListener('scroll', debounce(this.update, 80), { passive: true });
      window.addEventListener('resize', debounce(this.update, 150));
      this.update();
    }

    update() {
      const s = this.scroller;
      const max = s.scrollWidth - s.clientWidth;
      const pos = Math.abs(s.scrollLeft);
      const scrollable = max > 4;
      this.hidden = !scrollable;
      if (this.prev) this.prev.disabled = pos <= 4;
      if (this.next) this.next.disabled = pos >= max - 4;
    }
  }
  define('scroller-controls', ScrollerControls);

  /* ---------- Accordion group (optional single-open) ---------- */
  class AccordionGroup extends HTMLElement {
    connectedCallback() {
      if (this._bound || this.dataset.single !== 'true') return;
      this._bound = true;
      this.addEventListener(
        'toggle',
        (event) => {
          const target = event.target;
          if (!(target instanceof HTMLDetailsElement) || !target.open) return;
          this.querySelectorAll('details[open]').forEach((d) => {
            if (d !== target && d.parentElement.closest('accordion-group') === this) d.open = false;
          });
        },
        true
      );
    }
  }
  define('accordion-group', AccordionGroup);

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root) {
    const els = (root || document).querySelectorAll('.reveal:not(.is-visible)');
    if (!els.length) return;
    if (!document.body.classList.contains('has-reveal') || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -40px 0px', threshold: 0.05 }
    );
    els.forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-visible');
      else io.observe(el);
    });
    document.body.classList.add('reveal-ready');
  }

  /* ---------- Boot ---------- */
  function boot() {
    if (window.Shopify && window.Shopify.designMode) document.documentElement.classList.add('shopify-design-mode');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('video[autoplay]').forEach((v) => {
        try {
          v.pause();
          v.setAttribute('controls', '');
        } catch (e) {
          /* ignore */
        }
      });
    }
    initReveal();
    document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCart();
  });
})();
